import type { SearchResponse } from '@algolia/client-search'
import type { ComputedRef } from 'vue'
import {
  useAlgoliaIndexSearch,
  type AlgoliaIndexSearchParams,
  type UseAlgoliaIndexSearchReturnType,
} from '~/composables/algolia/useAlgoliaIndexSearch'
import { useSearchClient } from '~/composables/search/useSearchClient'
import {
  typesenseSearch,
  type TypesenseSearchParams,
} from '~/utils/typesenseClient'
import {
  adaptTypesenseSearchResponse,
  type NormalizedSearchResponse,
} from '~/utils/adaptTypesenseSearchResponse'
import { hasGeoSort, replaceGeoSortWithTextMatch } from '~/utils/typesenseGeo'
import { HITS_PER_PAGINATION } from '~/composables/algolia/constants'

export type TypesenseSearchOverrides = Omit<
  TypesenseSearchParams,
  'collection' | 'query_by'
>

export interface UseSearchIndexOptions {
  /** Typesense requires the searchable fields per request; Algolia configures these per-index instead. */
  typesenseQueryBy: string
  /**
   * Maps the caller's (algolia-shaped) requestOptions into a full typesense
   * params object (q/page/per_page/filter_by/sort_by/facet_by/...). Omit for
   * simple index lookups (e.g. useSearchEngineLocalities) — the default just
   * carries over pagination. Richer callers (the listing page) build one from
   * getTypesenseSearchOptions (KFT-4028).
   */
  toTypesenseParams?: (
    requestOptions: AlgoliaIndexSearchParams['requestOptions'],
    query: string
  ) => TypesenseSearchOverrides
  /**
   * Serve this surface's Typesense requests from the cluster search cache.
   * Requests sorted by the viewer's location are never cached — the coords
   * make each one unique per viewer.
   */
  typesenseCache?: boolean
  /**
   * Pick the engine the client resolved at boot (useSearchEngine
   * `clientOnly`), so a forced visitor never queries the other engine first.
   * Only for searches that never run during SSR/hydration.
   */
  clientOnly?: boolean
}

/**
 * Engine-aware replacement for useAlgoliaIndexSearch: same {result, search}
 * shape, so callers don't change. Algolia path delegates to the existing
 * composable untouched; Typesense path adapts to the same envelope.
 *
 * The engine is snapshotted at construction — callers rebuild the instance
 * when useSearchEngine() changes (see useTransactionalPageSearchSideEffects).
 */
export const useSearchIndex = <T>(
  indexName: string,
  {
    typesenseQueryBy,
    toTypesenseParams,
    typesenseCache,
    clientOnly,
  }: UseSearchIndexOptions
): UseAlgoliaIndexSearchReturnType<T> => {
  const handle = useSearchClient({ clientOnly })

  if (handle.engine === 'algolia') {
    return useAlgoliaIndexSearch<T>(indexName)
  }

  const result = useState<NormalizedSearchResponse<T> | null>(
    `${indexName}-search-result`,
    () => null
  )

  const search = async ({
    query,
    requestOptions,
  }: AlgoliaIndexSearchParams) => {
    // RequestOptionsObject (from @nuxtjs/algolia) doesn't type these, same as
    // the pre-existing gap in useTransactionalPageSearch.ts.
    const paginationOptions = requestOptions as
      | { hitsPerPage?: number; page?: number }
      | undefined
    const defaultPerPage = paginationOptions?.hitsPerPage ?? HITS_PER_PAGINATION
    const defaultAlgoliaPage = paginationOptions?.page ?? 0

    const overrides: TypesenseSearchOverrides = toTypesenseParams
      ? toTypesenseParams(requestOptions, query)
      : {
          q: query || '*',
          page: defaultAlgoliaPage + 1,
          per_page: defaultPerPage,
        }

    const perPage = overrides.per_page ?? defaultPerPage

    const params = {
      collection: indexName,
      query_by: typesenseQueryBy,
      ...overrides,
    }
    const clientOptions = (sortBy: string | undefined) => ({
      ...handle,
      useCache: typesenseCache && !hasGeoSort(sortBy),
    })
    let typesenseResult = await typesenseSearch<T>(
      params,
      clientOptions(params.sort_by)
    )

    // The `auto-facet-cities` curation rule flags itself when the query names
    // a city (scripts/create-typesense-facet-rules.mjs). The viewer's distance
    // sort would then rank events nearest the viewer rather than in that city,
    // so rerun with text match in its place — Algolia's city rule does the
    // same via `aroundLatLngViaIP: false`.
    const sortWithoutGeo =
      typesenseResult.metadata?.skip_geo_sort &&
      replaceGeoSortWithTextMatch(params.sort_by)
    if (sortWithoutGeo) {
      typesenseResult = await typesenseSearch<T>(
        { ...params, sort_by: sortWithoutGeo },
        clientOptions(sortWithoutGeo)
      )
    }

    const normalized = adaptTypesenseSearchResponse<T>(typesenseResult, {
      query: overrides.q,
      perPage,
    })
    result.value = normalized
    return normalized as unknown as SearchResponse<T>
  }

  onUnmounted(() => {
    result.value = null
  })

  return {
    result: computed(
      () => result.value
    ) as ComputedRef<SearchResponse<T> | null>,
    search,
  }
}
